import type { AppRouter } from '@server/trpc/routers/app.router';

import {
  createTRPCReact,
  createWSClient,
  httpBatchLink,
  splitLink,
  wsLink
} from '@trpc/react-query';
import { BehaviorSubject } from 'rxjs';

export const trpc = createTRPCReact<AppRouter>();

export type WsConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting';

export const wsConnectionStatus$ = new BehaviorSubject<WsConnectionStatus>(
  'connecting'
);

const getWsClient = () => {
  if (typeof window === 'undefined') return null;

  const isHttps = window.location.protocol === 'https:';
  const wsProtocol = isHttps ? 'wss' : 'ws';

  const wsUrl = import.meta.env.VITE_WS_URL
    ? `${wsProtocol}://${import.meta.env.VITE_WS_URL}/trpc`
    : `${wsProtocol}://${window.location.host}/trpc`;

  return createWSClient({
    url: wsUrl,
    retryDelayMs: (retryCount) => Math.min(retryCount * 1000, 5000),
    // Disable application-level keepAlive - rely on native WebSocket ping/pong
    // The native protocol-level ping/pong is handled automatically and doesn't require
    // the server to process application messages, so it works even when server is busy
    keepAlive: {
      // enabled: true,
      // intervalMs: 30000, // send ping every 30 seconds (reduced frequency)
      // pongTimeoutMs: 60000, // wait 60 seconds for pong response (allows server busy periods)
      enabled: false
    },
    onOpen: () => {
      wsConnectionStatus$.next('connected');
    },
    onError: (error) => {
      wsConnectionStatus$.next('reconnecting');
      console.error('WS connection error at', new Date().toISOString(), error);
    },
    onClose: () => {
      wsConnectionStatus$.next('reconnecting');
    },
    connectionParams: async () => {
      const token = localStorage.getItem('accessToken');
      // Add token refresh logic here if needed
      return {
        Authorization: token ? `Bearer ${token}` : ''
      };
    }
  });
};

export const createTrpcClient = () => {
  const wsClient = getWsClient();

  if (!wsClient) {
    // SSR/http-only path: there is no persistent WS to reconnect.
    wsConnectionStatus$.next('connected');
  }

  return trpc.createClient({
    links: [
      splitLink({
        condition(op) {
          return op.type === 'subscription';
        },
        true: wsClient
          ? wsLink({
              client: wsClient
            })
          : httpBatchLink({ url: '/trpc' }),
        false: httpBatchLink({
          url: '/trpc',
          headers() {
            const token =
              typeof window !== 'undefined'
                ? localStorage.getItem('accessToken')
                : null;
            return {
              Authorization: token ? `Bearer ${token}` : ''
            };
          }
        })
      })
    ]
  });
};
